import { FileField, TextInput } from 'react-admin';
import { useWatch } from 'react-hook-form';

import { DOCUMENTS_UPLOAD_LARGE_FILE_MAX_SIZE } from '@Common/constants/documents';
import { EditThirdPartyMandateDocument } from '@Common/types/bo/b2b/admins';

import { FileInput } from '@sh4res/bo-common/components';
import { UploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { FileInputValue } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

// On edit, the FileInput is prefilled with the admin's existing mandate document.
// Such a value carries the document `id` (and a viewer `src`) but no `rawFile`.
export type ExistingMandateDocumentValue = {
  id: number;
  src?: string;
  title?: string;
};

// Third-party legal structure and mandate document can only be set for third-party
// admins of level 3, and only from the internal back-office (not bo_external).
export function isThirdPartyLevel3(data: { is_third_party?: boolean; role?: string | null }) {
  return !isBoExternal() && data.is_third_party === true && data.role === 'admin_level_3';
}

export function ThirdPartyMandateFields({ testId }: { testId: string }) {
  const isThirdParty = useWatch({ name: 'is_third_party' });
  const role = useWatch({ name: 'role' });

  if (!isThirdPartyLevel3({ is_third_party: isThirdParty, role })) {
    return null;
  }

  return (
    <>
      <TextInput
        source="third_party_legal_structure"
        label="resources.bo_common.b2b.admins.fields.third_party_legal_structure"
        defaultValue={null}
        data-testid={`${testId}_LegalStructureInput`}
      />
      <FileInput
        source="third_party_mandate_document"
        label="resources.bo_common.b2b.admins.fields.third_party_mandate_document_id"
        accept="application/pdf"
        maxSize={DOCUMENTS_UPLOAD_LARGE_FILE_MAX_SIZE}
        data-testid={`${testId}_MandateDocumentInput`}>
        <FileField source="src" title="title" data-testid={`${testId}_MandateDocumentField`} />
      </FileInput>
    </>
  );
}

type ThirdPartyFormValues<T> = T & {
  is_third_party?: boolean;
  role?: string | null;
  third_party_legal_structure?: string | null;
  third_party_mandate_document?:
    | FileInputValue
    | ExistingMandateDocumentValue
    | EditThirdPartyMandateDocument
    | null;
};

// Uploads the mandate document file and normalizes the third-party fields into the
// request body shape expected by the backend. Fields are cleared (null) unless the
// admin is a level-3 third party. An untouched, prefilled document is kept as-is.
export async function transformThirdPartyMandate<T extends Record<string, unknown>>(
  data: ThirdPartyFormValues<T>,
  upload: UploadPresignedUrl,
) {
  if (!isThirdPartyLevel3(data)) {
    return {
      ...data,
      third_party_legal_structure: null,
      third_party_mandate_document: null,
    };
  }

  const file = data.third_party_mandate_document;

  if (!file) {
    // No document (never set, or removed): clear it
    return {
      ...data,
      third_party_legal_structure: data.third_party_legal_structure ?? null,
      third_party_mandate_document: null,
    };
  }

  if (!('rawFile' in file) || !(file.rawFile instanceof File)) {
    // Existing document left untouched: keep it as-is
    return {
      ...data,
      third_party_legal_structure: data.third_party_legal_structure ?? null,
      third_party_mandate_document: { keep_existing: true } as const,
    };
  }

  const uploadResult = await upload(file.rawFile);
  if (!uploadResult) {
    throw new Error('Error during upload');
  }

  return {
    ...data,
    third_party_legal_structure: data.third_party_legal_structure ?? null,
    third_party_mandate_document: {
      presigned_path: uploadResult.path,
      document_name: file.title,
    },
  };
}
